import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, LifeBuoy, ArrowRight } from 'lucide-react';
import { MarketingShell, MarketingHeader } from './MarketingShell';

/**
 * /resources — the audit checklist reference + support pointers.
 * Public, unauthenticated.
 */

// The live 12-point checklist (Point 4, Income Proof ML/HL, is currently
// disabled by policy, so it is omitted here).
const CHECKLIST: Array<{ n: number; label: string; note: string }> = [
  { n: 1, label: 'Documents Required', note: 'All mandatory documents present per product and applicant.' },
  { n: 2, label: 'CIBIL Score', note: 'Score meets the product threshold; hit/no-hit handled.' },
  { n: 3, label: 'Income Proof — PL / BL', note: 'Payslip / ITR income reconciles with CAM and bank credits.' },
  { n: 5, label: 'Novel Check', note: 'Banking surrogate: salary credits and average balance verified.' },
  { n: 6, label: 'Loan Eligibility Calculation', note: 'FOIR, LTV, amount, rate, tenure, and EMI recomputed and matched.' },
  { n: 7, label: 'Policy Level Checks', note: 'Hunter, BQS, RCU, Posidex, and loan-amount caps within policy.' },
  { n: 8, label: 'Deviation Matrix', note: 'Negative checks have documented, authorised-level deviations.' },
  { n: 9, label: 'Sanction Conditions', note: 'CAM and LOS conditions match; named documents are on file.' },
  { n: 10, label: 'Legal Detail', note: 'Legal status positive; property, owner, and address consistent.' },
  { n: 11, label: 'Valuation Report', note: 'Valuation amount matches and is within the 90-day window.' },
  { n: 12, label: 'Personal Discussion', note: 'PD conducted and recorded with substantive notes.' },
];

export const ResourcesPage: FC = () => (
  <MarketingShell>
    <MarketingHeader
      eyebrow="Resources"
      title="What the audit checks."
      lead="Agentic Loan Audit evaluates every loan file against this checklist. Each point reconciles the CAM against the LOS and the underlying documents, and produces an evidence-linked verdict."
    />

    <div className="mt-12">
      <h2 className="font-display text-title font-bold text-on-surface mb-4">The audit checklist</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {CHECKLIST.map((c) => (
          <div key={c.n} className="flex gap-3.5 bg-surface-container-lowest border border-outline-variant rounded-xl p-4">
            <span className="w-7 h-7 rounded-lg bg-secondary-container/60 text-primary font-display font-bold text-caption flex items-center justify-center shrink-0 tabular-nums">
              {c.n}
            </span>
            <div>
              <div className="text-label font-semibold text-on-surface">{c.label}</div>
              <div className="text-caption text-on-surface-variant leading-relaxed">{c.note}</div>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="bg-surface-container-lowest border border-outline-variant rounded-2xl p-6">
        <div className="w-10 h-10 rounded-xl bg-secondary-container/60 flex items-center justify-center text-primary">
          <BookOpen className="w-5 h-5" aria-hidden="true" />
        </div>
        <h3 className="mt-4 font-display text-heading font-bold text-on-surface">Documentation</h3>
        <p className="mt-1.5 text-label text-on-surface-variant leading-relaxed">
          Detailed guidance on each checkpoint, the dual-path verdict model, and how
          deviations and approver authority are evaluated. Available inside the app
          once you sign in.
        </p>
      </div>
      <div className="bg-surface-container-lowest border border-outline-variant rounded-2xl p-6">
        <div className="w-10 h-10 rounded-xl bg-secondary-container/60 flex items-center justify-center text-primary">
          <LifeBuoy className="w-5 h-5" aria-hidden="true" />
        </div>
        <h3 className="mt-4 font-display text-heading font-bold text-on-surface">Support</h3>
        <p className="mt-1.5 text-label text-on-surface-variant leading-relaxed">
          Access is managed by your Axis Finance administrator. For sign-in issues or
          role changes, reach out to your internal platform administrator.
        </p>
      </div>
    </div>

    <div className="mt-12 flex justify-center">
      <Link
        to="/login"
        className="h-11 px-6 inline-flex items-center gap-2 rounded-lg bg-primary text-on-primary text-label font-semibold hover:bg-primary-container transition-colors"
      >
        Get started
        <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </Link>
    </div>
  </MarketingShell>
);

export default ResourcesPage;
